import { Injectable } from '@angular/core';
import {environment} from "../../../environments/environment";
import {Observable, of} from "rxjs";
import {HttpClient, HttpParams} from "@angular/common/http";
import {catchError, map, switchMap} from "rxjs/operators";
import { Product } from '../../models/Product';
import {PaginatedResponse} from "../../models/paginatedResponse";
import { ProductHistoriquePayload } from '../../shared/erp-affichages/product-historique.types';
import { LogicielCaisseProductSyncService } from '../logiciel-caisse/logiciel-caisse-product-sync.service';

/** Options d'écriture produit ERP (sync caisse optionnelle). */
export interface AddProductOptions {
  /** Si true : ne pas pousser vers le logiciel caisse (ex. import XLSX qui sync en lot). */
  skipLogicielCaisseSync?: boolean;
}

export interface GammeListeBulkRequest {
  station_id: number;
  user_id: number;
  items: GammeListeBulkItem[];
}

/** Élément POST /api/products/liste/bulk */
export interface GammeListeBulkItem {
  type: 'gamme' | 'variante';
  nom_gamme: string;
  descrip: string;
  product_id: number;
}

/** Option ng-select — schéma + défauts + `includes` + `enriched_from_parametre_service`. */
export interface ProductAttributeOption {
  key: string;
  type?: string;
  default?: unknown;
  source: 'schema' | 'include' | 'enriched';
  includeDescription?: string;
}

/** Exclut les FK (*_id), la clé primaire id, et les horodatages techniques. */
function isExcludedSchemaKey(key: string): boolean {
  const k = key.trim();
  if (!k) {
    return true;
  }
  if (k === 'id') {
    return true;
  }
  if (k.endsWith('_id')) {
    return true;
  }
  if (k === 'created_at' || k === 'updated_at') {
    return true;
  }
  return false;
}

@Injectable({
  providedIn: 'root'
})
export class ProductsService {
    private apiUrl = environment.product_url;
    private baseUrl = `${this.apiUrl}api/products/`;


    constructor(
        private http: HttpClient,
        private lcProductSync: LogicielCaisseProductSyncService
    ) { }

  searchText = '';
  filters = {
    type: '',
    category: '',
    brand: '',
    priceOperator: '=',
    price: null,
    tva: '',
    tax: '',
    barcode: '',
    stockStatus: '',
    dateFrom: '',
    dateTo: ''
  };

  items = [

  ];

    getActiveProductsByStationId(stationId: number, page: number = 1, limit: number = 10, isArchive: boolean = false): Observable<PaginatedResponse<Product>> {
        const params = {
            page: page.toString(),
            limit: limit.toString(),
            is_archive: isArchive.toString()};

        return this.http.get<PaginatedResponse<Product>>(`${this.baseUrl}station/${stationId}`, { params });
    }

    getArchiveProductsByStationId(stationId: number, page: number = 1, limit: number = 10, isArchive: boolean = true): Observable<any[]> {
        const params = {
            page: page.toString(),
            limit: limit.toString(),
            is_archive: isArchive.toString()};

        return this.http.get<any[]>(`${this.baseUrl}${stationId}`, { params });
    }
    getProductsByStationId(stationId: number, page: number ,
                           limit: number, isArchive: boolean = false): Observable<PaginatedResponse<Product>> {
        const params = {
            page: page.toString(),
            limit: limit.toString(),
            is_archive: isArchive.toString()
        };

        return this.http.get<PaginatedResponse<Product>>(
            `${this.baseUrl}station/${stationId}`,
            { params }
        );
    }

    /**
     * GET /api/products/station/:id?ref_fabricant=…
     * Variantes (codes-barres) d'une désignation — chargement à la demande.
     */
    getProductsByRefFabricant(
        stationId: number,
        refFabricant: string,
        limit: number = 500
    ): Observable<Product[]> {
        const ref = String(refFabricant || '').trim();
        if (!stationId || !ref) {
            return of([]);
        }
        const params = new HttpParams()
            .set('page', '1')
            .set('limit', String(limit))
            .set('is_archive', 'false')
            .set('ref_fabricant', ref);
        return this.http
            .get<PaginatedResponse<Product>>(`${this.baseUrl}station/${stationId}`, { params })
            .pipe(
                map((res) => (Array.isArray(res?.data) ? res.data : [])),
                catchError(() => of([]))
            );
    }
    getProductsByEntrepotAndStation(entrepotId: number, stationId: number, page: number = 1, limit: number = 10, isArchive: boolean = false):
        Observable<PaginatedResponse<Product>> {
        const params = {
            page: page.toString(),
            limit: limit.toString(),
            is_archive: isArchive.toString()
        };

        return this.http.get<PaginatedResponse<Product>>(
            `${this.baseUrl}entrepot/${entrepotId}/station/${stationId}`,
            { params }
        );
    }

    getDetailsProduct (productId:number): Observable<Product> {
        return this.http.get<Product>(`${this.baseUrl}${productId}`);
    }

    /**
     * GET /api/products/by-code-barre?code_barre=&station_id=&user_id=
     */
    getProductsByCodeBarre(codeBarre: string, stationId: number, userId: number): Observable<any> {
        const params = new HttpParams()
            .set('code_barre', String(codeBarre).trim())
            .set('station_id', String(stationId))
            .set('user_id', String(userId));
        return this.http.get<any>(`${this.baseUrl}by-code-barre`, { params });
    }



    addProduct (
        stationId: number,
        product: any,
        options?: AddProductOptions
    ) : Observable<any> {
        const payload = {
            ...product,
            station_id: stationId
        };
        return this.http.post(`${this.baseUrl}`, payload).pipe(
            switchMap((res) => {
                if (options?.skipLogicielCaisseSync) {
                    return of(res);
                }
                return this.lcProductSync
                    .syncProductAfterErpCreate(payload as Record<string, unknown>)
                    .pipe(
                        map(() => res),
                        catchError(() => of(res))
                    );
            })
        );
    }

    postProductHistorique(payload: ProductHistoriquePayload): Observable<unknown> {
        return this.http.post(`${this.baseUrl}historique`, payload);
    }

    archiverProduct(productId: number): Observable<any> {
        const body: any = {
            is_archive: true
        };

        return this.http.patch(`${this.baseUrl}${productId}/archive`, body);
    }

    /** Retire client_id / user_id du corps PUT produit (non requis en mise à jour). */
    private buildProductPutBody(product: any, stationIdOverride?: number): Record<string, unknown> {
        const src = product && typeof product === 'object' ? { ...product } : {};
        delete src.client_id;
        delete src.user_id;
        if (stationIdOverride != null && Number(stationIdOverride) > 0) {
            src.station_id = stationIdOverride;
        }
        return src;
    }

    updateProduct(
        productId: number,
        product: any,
        mandatoryStationClient?: { station_id: number; client_id?: number }
    ): Observable<any> {
        const body = this.buildProductPutBody(
            product,
            mandatoryStationClient?.station_id
        );
        return this.http.put<any>(`${this.baseUrl}${productId}`, body);
    }

    /** Met à jour valeur_unitaire — PATCH /api/products/:id (sans client_id / user_id). */
    updateProductValeurUnitaire(
        productId: number,
        valeurUnitaire: number,
        _mandatoryStationClient?: { station_id: number; client_id?: number }
    ): Observable<any> {
        const value = Number(Number(valeurUnitaire).toFixed(3));
        return this.http.patch<any>(`${this.baseUrl}${productId}`, {
            valeur_unitaire: value,
        });
    }

    bulkAddProducts(products: any[], options?: AddProductOptions): Observable<any> {
        return this.http.post(`${this.baseUrl}bulk`, { products }).pipe(
            switchMap((res) => {
                if (options?.skipLogicielCaisseSync) {
                    return of(res);
                }
                return this.lcProductSync
                    .syncProductsAfterErpCreate((products || []) as Record<string, unknown>[])
                    .pipe(
                        map(() => res),
                        catchError(() => of(res))
                    );
            })
        );
    }

    /**
     * POST /api/products/sync-from-logiciel-caisse
     * Copie les produits caisse → ERP sans écraser les attributs déjà présents.
     */
    syncFromLogicielCaisse(
        stationId: number,
        clientId: number,
        products: any[]
    ): Observable<{
        success?: boolean;
        message?: string;
        summary?: {
            received: number;
            created: number;
            skipped_existing: number;
            skipped_invalid: number;
        };
        errors?: Array<{ index: number; error: string }>;
    }> {
        return this.http.post(`${this.baseUrl}sync-from-logiciel-caisse`, {
            station_id: stationId,
            client_id: clientId,
            products,
        });
    }

    /**
     * POST /api/products/liste/bulk — enregistrement groupé de gammes / variantes.
     */
    bulkAddListe(payload: GammeListeBulkRequest): Observable<any> {
        return this.http.post(`${this.baseUrl}liste/bulk`, payload);
    }

    /**
     * GET /api/products/attributes — `schema` (+ `defaults`), `get_by_id.includes`, `get_by_id.enriched_from_parametre_service`.
     * Exclut les attributs `*_id`, `id`, `created_at`, `updated_at`.
     */
    getProductAttributes(): Observable<ProductAttributeOption[]> {
        return this.http.get<unknown>(`${this.baseUrl}attributes`).pipe(map((raw) => this.parseProductAttributeOptions(raw)));
    }

    private parseProductAttributeOptions(raw: unknown): ProductAttributeOption[] {
        const out: ProductAttributeOption[] = [];

        if (Array.isArray(raw)) {
            for (const x of raw) {
                if (typeof x === 'string') {
                    const k = x.trim();
                    if (k && !isExcludedSchemaKey(k)) {
                        out.push({ key: k, source: 'schema' });
                    }
                    continue;
                }
                if (x && typeof x === 'object') {
                    const row = x as { key?: string; type?: string };
                    const k = row.key != null ? String(row.key).trim() : '';
                    if (!k || isExcludedSchemaKey(k)) {
                        continue;
                    }
                    const t = row.type != null ? String(row.type) : undefined;
                    out.push({ key: k, type: t, source: 'schema' });
                }
            }
            out.sort((a, b) => a.key.localeCompare(b.key, 'fr'));
            return out;
        }

        if (!raw || typeof raw !== 'object') {
            return out;
        }

        const o = raw as Record<string, unknown>;
        const defaults =
            o['defaults'] && typeof o['defaults'] === 'object' ? (o['defaults'] as Record<string, unknown>) : undefined;
        const getById =
            o['get_by_id'] && typeof o['get_by_id'] === 'object' ? (o['get_by_id'] as Record<string, unknown>) : undefined;
        const includesRaw = getById && Array.isArray(getById['includes']) ? getById['includes'] : [];
        const enrichedRaw =
            getById && Array.isArray(getById['enriched_from_parametre_service'])
                ? getById['enriched_from_parametre_service']
                : [];

        const nested = o['data'] && typeof o['data'] === 'object' ? (o['data'] as Record<string, unknown>) : null;
        const schema =
            (Array.isArray(o['schema']) ? o['schema'] : null) ??
            (nested && Array.isArray(nested['schema']) ? nested['schema'] : null);

        if (Array.isArray(schema)) {
            for (const row of schema) {
                if (!row || typeof row !== 'object') {
                    continue;
                }
                const r = row as { key?: string; type?: string };
                const k = r.key != null ? String(r.key).trim() : '';
                if (!k || isExcludedSchemaKey(k)) {
                    continue;
                }
                const t = r.type != null ? String(r.type) : undefined;
                const def = defaults ? defaults[k] : undefined;
                out.push({
                    key: k,
                    type: t,
                    default: def,
                    source: 'schema'
                });
            }
        }

        const usedKeys = new Set(out.map((x) => x.key));
        for (const inc of includesRaw) {
            if (!inc || typeof inc !== 'object') {
                continue;
            }
            const row = inc as { association?: string; description?: string };
            const key = row.association != null ? String(row.association).trim() : '';
            if (!key || usedKeys.has(key)) {
                continue;
            }
            usedKeys.add(key);
            out.push({
                key,
                type: 'association',
                source: 'include',
                includeDescription: row.description != null ? String(row.description) : undefined
            });
        }

        for (const rawEnriched of enrichedRaw) {
            const key =
                typeof rawEnriched === 'string'
                    ? rawEnriched.trim()
                    : rawEnriched != null
                      ? String(rawEnriched).trim()
                      : '';
            if (!key || usedKeys.has(key)) {
                continue;
            }
            usedKeys.add(key);
            out.push({
                key,
                type: 'Enrichi',
                source: 'enriched',
                includeDescription: 'Données enrichies depuis le service paramètre'
            });
        }

        const sourceOrder = (s: ProductAttributeOption['source']) =>
            s === 'schema' ? 0 : s === 'include' ? 1 : s === 'enriched' ? 2 : 3;

        out.sort((a, b) => {
            const sa = sourceOrder(a.source);
            const sb = sourceOrder(b.source);
            if (sa !== sb) {
                return sa - sb;
            }
            return a.key.localeCompare(b.key, 'fr');
        });

        return out;
    }

    /** Produit financier « Recette » (consolidation tickets caisse) — créé côté ERP si absent. */
    ensureRecetteProduct(
        stationId: number,
        clientId: number,
        tvaId?: number | null
    ): Observable<{ product: Product; created?: boolean }> {
        const url = `${this.baseUrl}ensure-recette`;
        return this.http
            .post<{ product: Product; created?: boolean; message?: string }>(url, {
                station_id: stationId,
                client_id: clientId,
                tva_id: tvaId ?? null,
            })
            .pipe(map((res) => ({ product: res.product, created: res.created })));
    }

    /** Liste des produits ERP de type « financier » d'une station (Recette, etc.). */
    getFinancialProductsByStation(stationId: number): Observable<Product[]> {
        return this.getActiveProductsByStationId(stationId, 1, 200).pipe(
            map((res) => {
                const list = (res?.data as Product[] | undefined) ?? [];
                return list.filter(
                    (p) => String((p as { type?: string }).type ?? '').trim() === 'financier'
                );
            })
        );
    }

    /**
     * Liste des produits financiers en garantissant la présence de « Recette ».
     * Crée « Recette » si nécessaire puis liste tous les produits « financier » de la station.
     * Ainsi le choix « Produits financiers » n'est jamais vide dans le modal de facturation.
     */
    getFinancialProductsEnsuringRecette(
        stationId: number,
        clientId: number,
        tvaId?: number | null
    ): Observable<Product[]> {
        return this.ensureRecetteProduct(stationId, clientId, tvaId).pipe(
            switchMap((recette) =>
                this.getFinancialProductsByStation(stationId).pipe(
                    map((list) => {
                        const recetteProduct = recette?.product;
                        if (!recetteProduct) {
                            return list;
                        }
                        const recetteId = Number((recetteProduct as { id?: number }).id);
                        const alreadyListed = list.some(
                            (p) => Number((p as { id?: number }).id) === recetteId
                        );
                        return alreadyListed ? list : [recetteProduct, ...list];
                    })
                )
            )
        );
    }
}
